// Pagina PERSONAL TASKS - Obiettivi / Task personali con Kanban
import { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { PageContainer, KanbanPageContainer } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select, Field } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Badge } from '@/components/ui/Misc';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
import { usePersonalTasksStore } from '@/store/personalTasksStore';
import { toast } from '@/hooks/useToast';
import { confirmDialog } from '@/components/ui/Confirm';
import { useI18n } from '@/i18n';
import { PERSONAL_COLUMNS, PRIORITY_META, PRIORITIES, PERSONAL_STATUS_META } from '@/utils/constants';
import { cn, formatDate, daysUntil, toDatetimeLocalValue, fromDatetimeLocalValue, nowISO } from '@/utils';
import type { PersonalTask, TaskStatus, Priority } from '@/types';
import { Target, Plus, Trash2, Calendar, Flag } from 'lucide-react';

export function PersonalTasksPage() {
  const { t } = useI18n();
  const { tasks, load, create, update, remove, move, reorder } = usePersonalTasksStore();
  const [editing, setEditing] = useState<PersonalTask | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createStatus, setCreateStatus] = useState<TaskStatus>('todo');

  useEffect(() => {
    load();
  }, [load]);

  function handleAdd(status: TaskStatus) {
    setCreateStatus(status);
    setShowCreate(true);
  }

  // Kolonne tradotte
  const columns = PERSONAL_COLUMNS.map((c) => ({
    ...c,
    title: t(`personal.status_${c.status}`),
  }));

  return (
    <>
      <Header
        title={t('personal.title')}
        subtitle={t('personal.subtitle', {
          active: tasks.filter((ts) => ts.status !== 'done' && ts.status !== 'cancelled').length,
          done: tasks.filter((ts) => ts.status === 'done').length,
        })}
        icon={<div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center"><Target size={18} className="text-blue-600 dark:text-blue-400" /></div>}
        actions={
          <Button size="sm" onClick={() => { setCreateStatus('todo'); setShowCreate(true); }}>
            <Plus size={16} /> {t('common.new')}
          </Button>
        }
      />

      <KanbanPageContainer className="!px-0 !py-3">
        {tasks.length === 0 ? (
          <div className="overflow-y-auto">
            <EmptyState
              icon={<Target size={48} strokeWidth={1.5} />}
              title={t('personal.empty_title')}
              description={t('personal.empty_desc')}
              action={<Button onClick={() => setShowCreate(true)}><Plus size={16} /> {t('common.new')}</Button>}
            />
          </div>
        ) : (
          <KanbanBoard<PersonalTask>
            columns={columns}
            items={tasks}
            onMove={(id, status, position) => move(id, status as TaskStatus, position)}
            onReorder={(status, ids) => reorder(status as TaskStatus, ids)}
            onEdit={(item) => setEditing(item)}
            onAdd={handleAdd}
            renderCard={(item) => <PersonalCard item={item} />}
          />
        )}
      </KanbanPageContainer>

      {showCreate && (
        <PersonalEditor
          initialStatus={createStatus}
          onClose={() => setShowCreate(false)}
          onSave={async (data) => {
            await create(data);
            toast.success(t('personal.created'));
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <PersonalEditor
          task={editing}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            await update(editing.id, data);
            toast.success(t('personal.updated'));
            setEditing(null);
          }}
          onDelete={async () => {
            confirmDialog({
              title: t('personal.delete_confirm_title'),
              message: t('personal.delete_confirm_msg'),
              confirmText: t('common.delete'),
              danger: true,
              onConfirm: async () => {
                await remove(editing.id);
                toast.success(t('personal.deleted'));
                setEditing(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------
// Card
// ------------------------------------------------------------
function PersonalCard({ item }: { item: PersonalTask }) {
  const { t } = useI18n();
  const priority = PRIORITY_META[item.priority];
  const days = daysUntil(item.due_date);
  const overdue = days !== null && days < 0 && item.status !== 'done' && item.status !== 'cancelled';
  const soon = days !== null && days >= 0 && days <= 2 && item.status !== 'done' && item.status !== 'cancelled';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/60 p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing">
      <div className="flex items-start gap-2 mb-1.5">
        <span className={cn('shrink-0 w-1.5 h-1.5 rounded-full mt-1.5', priority.dot)} />
        <h4 className="text-sm font-medium text-slate-900 dark:text-white flex-1 line-clamp-2">{item.title || t('notes.untitled')}</h4>
      </div>

      {item.description && (
        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">{item.description}</p>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <Badge className={cn('text-[10px]', priority.color)}>
          <Flag size={9} /> {t(`personal.priority_${item.priority}`)}
        </Badge>
        {item.due_date && (
          <Badge className={cn('text-[10px]', overdue ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' : soon ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300')}>
            <Calendar size={9} /> {formatDate(item.due_date)}
          </Badge>
        )}
        {item.tags.slice(0, 2).map((tg) => (
          <Badge key={tg} className="text-[10px] bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">#{tg}</Badge>
        ))}
      </div>

      {overdue && (
        <p className="text-[10px] text-rose-500 font-medium mt-1.5">{t('personal.overdue', { days: Math.abs(days!) })}</p>
      )}
      {soon && (
        <p className="text-[10px] text-amber-500 font-medium mt-1.5">{t('personal.due_soon', { days: days! })}</p>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Editor
// ------------------------------------------------------------
function PersonalEditor({
  task,
  initialStatus,
  onClose,
  onSave,
  onDelete,
}: {
  task?: PersonalTask;
  initialStatus?: TaskStatus;
  onClose: () => void;
  onSave: (data: Partial<PersonalTask>) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? initialStatus ?? 'todo');
  const [priority, setPriority] = useState<Priority>(task?.priority ?? 'medium');
  const [dueDate, setDueDate] = useState(task?.due_date ?? null);
  const [tagsInput, setTagsInput] = useState(task?.tags.join(', ') ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const tags = tagsInput.split(',').map((tg) => tg.trim().replace(/^#/, '')).filter(Boolean);
      await onSave({
        title, description, status, priority, due_date: dueDate, tags,
        updated_at: nowISO(),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={task ? t('personal.editor_edit') : t('personal.editor_new')}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {onDelete && task ? (
            <Button variant="danger" size="sm" onClick={onDelete}>
              <Trash2 size={14} /> {t('common.delete')}
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button loading={saving} onClick={handleSave} disabled={!title.trim()}>{t('common.save')}</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('personal.field_title')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('personal.field_title_ph')} autoFocus />
        </Field>

        <Field label={t('personal.field_desc')}>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('personal.field_desc_ph')} rows={3} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('personal.field_status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
              {(Object.keys(PERSONAL_STATUS_META) as TaskStatus[]).map((s) => (
                <option key={s} value={s}>{t(`personal.status_${s}`)}</option>
              ))}
            </Select>
          </Field>

          <Field label={t('personal.field_priority')}>
            <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{t(`personal.priority_${p}`)}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t('personal.field_due')}>
          <Input
            type="datetime-local"
            value={toDatetimeLocalValue(dueDate)}
            onChange={(e) => setDueDate(fromDatetimeLocalValue(e.target.value))}
          />
        </Field>

        <Field label={t('personal.field_tags')}>
          <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder={t('notes.field_tags_ph')} />
        </Field>
      </div>
    </Modal>
  );
}
